import { Dictionary, ListResponse, SafeAny } from '@models/common';
import { Classification, TaxRate } from '@models/product';
import {
  Affiliate,
  BillingStatus,
  BillingType,
  Company,
  ECompanyStatus,
  Subscription,
  TenantList,
  UserDetail,
  UserInfo,
  UserListRequest
} from '@models/settings';
import { AdminConfig, PRICING } from '@swyft/ui';
import { differenceInDays, isValid } from 'date-fns';
import API from './http';
import { EStorageKeys, storageSet } from './storage';
interface UpdatePriceBaseSettingPayload {
  id: string;
  key: string;
  value: string;
}

export interface UpdateCurrencyPayload {
  nameId: string;
  symbolId: string;
  name: string;
  symbol: string;
}

export interface CreateSubscriptionPayload {
  subscriptionId: string;
  successUrl: string;
  cancelUrl: string;
}

export interface RedirectStripeCustomerPostalPayload {
  companyId: string;
  successUrl: string;
}

export const getListClassification = (): Promise<Classification[]> =>
  API.get('/api/pricing/Classification').then((res) => res.data.items);

export const createClassification = (payload: Omit<Classification, 'id'>): Promise<Classification> =>
  API.post('/api/pricing/Classification', payload).then((res) => res.data);

export const updateClassification = (payload: Classification): Promise<Classification> =>
  API.put('/api/pricing/Classification', payload).then((res) => res.data);

export const getListTaxRate = (): Promise<TaxRate[]> => API.get('/api/pricing/TaxRate').then((res) => res.data.items);

export const createTaxRate = (payload: Omit<TaxRate, 'id'>): Promise<TaxRate> =>
  API.post('/api/pricing/TaxRate', payload).then((res) => res.data);

export const updateTaxRate = (payload: TaxRate): Promise<TaxRate> =>
  API.put('/api/pricing/TaxRate', payload).then((res) => res.data);

export const getTenantCurrency = () => {
  return API.get('/api/pricing/public/config').then((res) => {
    storageSet(EStorageKeys.CURRENCY, res.data);
    return res.data;
  });
};

export const getCurrencySettings = () => {
  return API.get('/api/pricing/BaseSettings').then((res) => res.data);
};

export const updatePriceBaseSetting = ({ id, ...rest }: UpdatePriceBaseSettingPayload) => {
  return API.put(`/api/pricing/BaseSettings/${id}`, rest);
};

export const getAdminConfig = async (): Promise<AdminConfig> => {
  return API.get(`/api/admin/client/config`).then((res) => res.data);
};

export const updatePaypalMail = (PaypalAddress: string) => {
  return API.put(`/api/admin/client/config/affiliate-payout`, {
    PaypalAddress
  }).then((res) => res.data);
};

export const getSubscriptions = (): Promise<Subscription[]> => {
  return API.get(`/api/admin/client/subscription/available`).then((res) => res.data);
};

export const getCompanySubscriptions = (): Promise<SafeAny> => {
  return API.get(`/api/admin/client/subscription/company`).then((res) => {
    const { data } = res;
    const monthlySubscription = data?.find(
      (item: SafeAny) => item.status === BillingStatus.Active && item.paymentType === BillingType.Monthly
    );
    const annuallySubscription = data?.find(
      (item: SafeAny) => item.status === BillingStatus.Active && item.paymentType === BillingType.Annually
    );
    const cancelSubscription = data?.find((item: SafeAny) => item.status === BillingStatus.Cancelled);
    if (annuallySubscription) {
      return annuallySubscription;
    }
    if (monthlySubscription) {
      return monthlySubscription;
    }
    return cancelSubscription || null;
  });
};

export const createSubscription = (payload: CreateSubscriptionPayload): Promise<SafeAny> => {
  return API.post(`/api/admin/client/subscription`, payload).then((res) => res.data);
};

export const getSubscriptionConfirm = (): Promise<SafeAny> => {
  return API.get(`/api/admin/client/subscription/refresh`).then((res) => res.data);
};

export const redirectStripeCustomerPostal = (payload: RedirectStripeCustomerPostalPayload): Promise<SafeAny> => {
  return API.post(`/api/admin/client/subscription/portal`, payload).then((res) => res.data);
};

export const getCompany = (): Promise<Company> => {
  return API.get(`/api/admin/client/company/tenant`).then((res) => res.data);
};

export const getCompanyInformation = async () => {
  const companyInfo = await getCompany();
  const { trialEnd } = companyInfo;
  let isEnabledSubscription = true;
  let isCompanySuspended = false;
  if (companyInfo.status === ECompanyStatus.Inactive) {
    isEnabledSubscription = false;
  } else if (companyInfo.status === ECompanyStatus.Trial && trialEnd && isValid(new Date(trialEnd))) {
    if (differenceInDays(new Date(trialEnd), new Date()) < 0) {
      isEnabledSubscription = false;
    }
  }
  if (companyInfo.status === ECompanyStatus.Suspended) {
    isCompanySuspended = true;
  }
  return { isEnabledSubscription, isCompanySuspended, companyInfo };
};

export const getUserList = async (params: Partial<UserListRequest>): Promise<ListResponse<UserDetail>> => {
  const response = await API.get(`/api/accounts/clientadmin/user`, {
    params
  });
  return response?.data;
};

export const getTenantByUser = async (): Promise<TenantList[]> => {
  const response = await API.get(`/api/accounts/clientadmin/user/tenants`);
  return response?.data;
};

export const getTapfiliate = async (): Promise<Affiliate> => {
  const response = await API.get('/api/admin/client/config/tapfiliate');
  return response.data;
};

export const updateAdminConfigFeatures = ({
  serviceName,
  featureName,
  payload
}: {
  serviceName: string;
  featureName: string;
  payload: {
    Enabled: boolean;
    FeatureConfig: Dictionary;
  };
}) => {
  return API.put(`/api/admin/client/config/service/${serviceName}/feature/${featureName}`, payload);
};

export const updateAdminConfigSetting = ({
  serviceName,
  settingName,
  value
}: {
  serviceName: string;
  settingName: string;
  value: string;
}) => {
  const payload = { Value: value };
  return API.put(`/api/admin/client/config/service/${serviceName}/setting/${settingName}`, payload);
};

export const updateTenantCurrency = ({ name, symbol }: { name: string; symbol: string }) => {
  const updateNamePromise = updateAdminConfigSetting({
    serviceName: PRICING,
    settingName: 'Currency.Name',
    value: name
  });
  const updateSymbolPromise = updateAdminConfigSetting({
    serviceName: PRICING,
    settingName: 'Currency.Symbol',
    value: symbol
  });

  return Promise.all([updateNamePromise, updateSymbolPromise]);
};

export const getUserInfo = async (): Promise<UserInfo> => {
  const response = await API.get('/api/accounts/clientadmin/user/info');
  return response.data;
};

export const updateUserInfo = async (payload: UserInfo) => {
  const response = await API.put('/api/accounts/clientadmin/user/info', payload);
  return response.data;
};
